import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import {
  applyAdminFilters,
  getAdminNavLinks,
  readAdminFiltersFromSearch,
  type AdminFilters,
} from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/logins')({
  component: AdminLoginsRoute,
})

function AdminLoginsRoute() {
  const [token] = useState(getAdminToken)
  const [filters, setFilters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.adminLogins>>['items']>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])
  const loginStatus =
    filters.status === 'success' || filters.status === 'failed' ? filters.status : 'all'

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!token) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const data = await api.adminLogins(token, {
          limit: 250,
          ...(loginStatus === 'success' || loginStatus === 'failed' ? { status: loginStatus } : {}),
        })
        setRows(data.items)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load logins.')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [loginStatus, token])

  const filteredRows = useMemo(() => {
    let next = rows
    if (filters.country.trim()) {
      const country = filters.country.trim().toUpperCase()
      next = next.filter((row) => (row.ipCountryCode ?? '').toUpperCase() === country)
    }
    if (filters.query.trim()) {
      const q = filters.query.trim().toLowerCase()
      next = next.filter(
        (row) => row.userId.toLowerCase().includes(q) || row.email.toLowerCase().includes(q)
      )
    }
    return next
  }, [filters.country, filters.query, rows])

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Logins Feed</h1>
        <p className={styles.subtitle}>Monitor auth success/failure patterns with geo details.</p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
        <div className={styles.row}>
          <label>
            Status
            <select
              className={styles.select}
              value={loginStatus}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  status:
                    event.target.value === 'success' || event.target.value === 'failed'
                      ? event.target.value
                      : 'all',
                }))
              }
            >
              <option value="all">All</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label>
            Country
            <input
              className={styles.input}
              value={filters.country}
              onChange={(event) =>
                setFilters((current) => ({ ...current, country: event.target.value }))
              }
              placeholder="US"
            />
          </label>
          <label>
            Search
            <input
              className={styles.input}
              value={filters.query}
              onChange={(event) =>
                setFilters((current) => ({ ...current, query: event.target.value }))
              }
              placeholder="user id or email"
            />
          </label>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Apply Filters
          </button>
          <button
            className={styles.ghostButton}
            type="button"
            onClick={() =>
              applyAdminFilters({
                ...filters,
                status: 'all',
                country: '',
                query: '',
              })
            }
          >
            Reset Login Filters
          </button>
        </div>
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && isLoading && (
        <section className={styles.card}>
          <p className={styles.muted}>Loading logins...</p>
        </section>
      )}

      {token && error && (
        <section className={styles.card}>
          <p className={styles.error}>{error}</p>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Retry
          </button>
        </section>
      )}

      {token && !isLoading && !error && (
        <section className={styles.card}>
          <h2>Rows</h2>
          {filteredRows.length === 0 ? (
            <p className={styles.emptyState}>No login events in this range.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Failure Reason</th>
                  <th>Geo</th>
                  <th>Device</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.userId}</td>
                    <td>{row.status}</td>
                    <td>{row.failureReason ?? '-'}</td>
                    <td>
                      {row.ipCountryCode ?? '-'} {row.ipRegionCode ?? ''} {row.ipCity ?? ''}
                    </td>
                    <td>{row.deviceType ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  )
}
